import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { NavigationComponent } from 'common/navigation/navigation.component';
import { AppService } from 'app/app.service';
import { filter, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiManufacturer, ApiV1Service, NamedTenant, ViewService } from 'api';
import { MatDialog } from '@angular/material/dialog';
import { AppHeaderComponent } from 'app/app-header.component';
import { adminOrgConfig } from 'app/auth/auth.config';
import { SwitchTenantDialogComponent } from 'app/switch-tenant-dialog.component';

@Component({
  selector: 'app-root',
  imports: [TranslateModule, AppHeaderComponent, NavigationComponent, RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  protected readonly title = signal('frontend');
  protected loginResponse = signal<LoginResponse | null>(null);
  private readonly apiV1Service = inject(ApiV1Service);
  private readonly viewService = inject(ViewService);
  private readonly router = inject(Router);
  private destroyRef = inject(DestroyRef);

  private readonly oidcSecurityService = inject(OidcSecurityService);
  protected readonly appService = inject(AppService);

  protected readonly manufacturer = this.appService.manufacturer;
  protected readonly isAdminMode = computed(() => {
    const adminTenantId = this.appService.adminTenantId();
    const userOrg = this.appService.user()?.org;
    return adminTenantId != null && userOrg != null && adminTenantId !== userOrg;
  });

  readonly dialog = inject(MatDialog);

  ngOnInit() {
    this.oidcSecurityService.checkAuth().subscribe(async (loginResponse: LoginResponse) => {
      this.loginResponse.set(loginResponse);
      const { userData } = loginResponse;

      this.appService.user.set(userData);

      this.loadCurrentManufacturer().subscribe();
    });
  }

  storeManufacturer(manufacturer: ApiManufacturer) {
    this.appService.manufacturer.set(manufacturer);
  }

  login() {
    this.oidcSecurityService.authorize();
  }

  logout() {
    this.oidcSecurityService.logoff().subscribe((result) => console.log(result));
  }

  switchTenant() {
    this.viewService
      .getTenantList()
      .pipe(
        switchMap((tenants: NamedTenant[]) =>
          this.dialog
            .open(SwitchTenantDialogComponent, {
              data: {
                tenants: tenants,
                currentTenant: this.determineCurrentTenant(),
              },
            })
            .afterClosed(),
        ),
        filter(
          (tenant: NamedTenant | undefined): tenant is NamedTenant & { tenantId: string } =>
            typeof tenant?.tenantId === 'string' && tenant.tenantId.length > 0,
        ),
        tap((tenant) => this.appService.adminTenantId.set(tenant.tenantId)),
        switchMap(() => this.loadCurrentManufacturer()),
      )
      .subscribe(() => {
        void this.router.navigateByUrl('/dashboard');
      });
  }

  private loadCurrentManufacturer() {
    return this.apiV1Service.getCurrentManufacturer().pipe(
      tap((manufacturer: ApiManufacturer) => this.storeManufacturer(manufacturer)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  private determineCurrentTenant(): string | null {
    const actualUserTenant = this.appService.user()?.org;
    const currentAdminTenant = this.appService.adminTenantId();
    return currentAdminTenant != null ? currentAdminTenant : (actualUserTenant ?? null);
  }

  protected readonly adminOrgConfig = adminOrgConfig;
}
